import { DEFAULT_SEO, SITE_NAME, SITE_URL } from './siteConfig';

export interface PageSeoInput {
  title: string;
  description: string;
  path?: string;
  ogImage?: string;
  locale?: 'de_DE' | 'en_US';
  noindex?: boolean;
  jsonLd?: object | object[];
}

const JSON_LD_ROOT_ID = 'zl-page-jsonld';

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel: string, href: string) {
  let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
}

function upsertJsonLd(data: object | object[] | null) {
  const existing = document.getElementById(JSON_LD_ROOT_ID);
  if (!data) {
    existing?.remove();
    return;
  }

  const payload = Array.isArray(data)
    ? { '@context': 'https://schema.org', '@graph': data }
    : data;

  const el = (existing ?? document.createElement('script')) as HTMLScriptElement;
  if (!existing) {
    el.id = JSON_LD_ROOT_ID;
    el.type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(payload);
}

export function applyPageSeo(input: PageSeoInput) {
  const path = input.path ?? window.location.pathname;
  const canonical = path === '/' ? `${SITE_URL}/` : `${SITE_URL}${path}`;
  const ogImage = input.ogImage ?? DEFAULT_SEO.ogImage;
  const locale = input.locale ?? 'de_DE';

  document.title = input.title;

  upsertMeta('name', 'description', input.description);
  upsertMeta('name', 'robots', input.noindex ? 'noindex, nofollow' : 'index, follow');

  upsertMeta('property', 'og:type', 'website');
  upsertMeta('property', 'og:site_name', SITE_NAME);
  upsertMeta('property', 'og:title', input.title);
  upsertMeta('property', 'og:description', input.description);
  upsertMeta('property', 'og:url', canonical);
  upsertMeta('property', 'og:image', ogImage);
  upsertMeta('property', 'og:locale', locale);

  upsertMeta('name', 'twitter:card', 'summary_large_image');
  upsertMeta('name', 'twitter:title', input.title);
  upsertMeta('name', 'twitter:description', input.description);
  upsertMeta('name', 'twitter:image', ogImage);

  upsertLink('canonical', canonical);

  if (input.jsonLd) {
    upsertJsonLd(input.jsonLd);
  } else {
    upsertJsonLd(null);
  }
}

export function applyDefaultPageSeo(options?: { noindex?: boolean; locale?: 'de_DE' | 'en_US' }) {
  applyPageSeo({
    title: DEFAULT_SEO.title,
    description: DEFAULT_SEO.description,
    path: '/',
    noindex: options?.noindex,
    locale: options?.locale,
  });
}
